import React, { useState } from 'react';
import './RoutineStyles.css';

function RoutinePlayer({ routine, onExit, onMinimize }) {
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [workoutData, setWorkoutData] = useState({}); // Store results: { exerciseId: { set1: { weight, done }, ... } }

    const currentExercise = routine[currentStepIndex];
    const isLastExercise = currentStepIndex === routine.length - 1;

    // Helper to get stats for the current exercise
    const getExerciseData = (exId) => workoutData[exId] || {};

    const handleSetToggle = (exId, setParam, isChecked) => {
        setWorkoutData(prev => ({
            ...prev,
            [exId]: {
                ...prev[exId],
                [setParam]: {
                    ...(prev[exId]?.[setParam] || {}),
                    completed: isChecked
                }
            }
        }));
    };

    const handleWeightChange = (exId, setParam, val) => {
        setWorkoutData(prev => ({
            ...prev,
            [exId]: {
                ...prev[exId],
                [setParam]: {
                    ...(prev[exId]?.[setParam] || {}),
                    weight: val
                }
            }
        }));
    };

    const handleNext = () => {
        if (isLastExercise) {
            onExit(); // Finish
        } else {
            setCurrentStepIndex(prev => prev + 1);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    if (!currentExercise) return <div className="player-error">No hay ejercicios cargados.</div>;

    const setsArray = Array.from({ length: currentExercise.defaults.series }, (_, i) => i + 1);

    return (
        <div className="routine-player">
            {/* Header Navigation */}
            <header className="player-header">
                <button className="icon-button" onClick={onMinimize} aria-label="Minimizar">⏬</button>
                <div className="player-progress">
                    Ejercicio {currentStepIndex + 1} de {routine.length}
                </div>
                <button className="icon-button" onClick={onExit} aria-label="Salir">✕</button>
            </header>

            {/* Main Visual: Video & Muscle Map */}
            <section className="player-visuals">
                <div className="video-container">
                    {/* Using iframe for Youtube embeds based on data */}
                    <iframe
                        src={currentExercise.videoUrl}
                        title={currentExercise.name}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                    ></iframe>
                </div>
                <div className="muscle-badge">
                    <img src={currentExercise.muscleMap} alt="Muscle Target" />
                </div>
            </section>

            {/* Exercise Info */}
            <section className="player-info">
                <div className="info-head">
                    <span className="info-tag">{currentExercise.muscleGroup}</span>
                    <h2 className="info-title">{currentExercise.name}</h2>
                </div>

                <div className="stat-grid">
                    <div className="stat-item">
                        <span className="stat-val">{currentExercise.defaults.series}</span>
                        <span className="stat-lbl">Series</span>
                    </div>
                    <div className="stat-item">
                        <span className="stat-val">{currentExercise.defaults.reps}</span>
                        <span className="stat-lbl">Reps</span>
                    </div>
                    <div className="stat-item">
                        <span className="stat-val">{currentExercise.defaults.tempo}</span>
                        <span className="stat-lbl">Tempo</span>
                    </div>
                    <div className="stat-item">
                        <span className="stat-val">{currentExercise.defaults.rest}"</span>
                        <span className="stat-lbl">Descanso</span>
                    </div>
                </div>

                <div className="instructions-box">
                    <details>
                        <summary>Instrucciones Técnicas</summary>
                        <ul>
                            {currentExercise.instructions.map((line, idx) => <li key={idx}>{line}</li>)}
                        </ul>
                    </details>
                </div>
            </section>

            {/* Logging Section */}
            <section className="player-logging">
                <h3 className="section-title-sm">Registro de Actividad</h3>
                <div className="logging-list">
                    {setsArray.map(setNum => {
                        const setKey = `set-${setNum}`;
                        const currentSetData = getExerciseData(currentExercise.id)[setKey] || {};
                        const isDone = currentSetData.completed;

                        return (
                            <div key={setNum} className={`logging-row ${isDone ? 'is-done' : ''}`}>
                                <div className="log-label">Serie {setNum}</div>
                                <div className="log-input-group">
                                    <input
                                        type="number"
                                        className="log-input"
                                        placeholder={`${currentExercise.defaults.weight}`}
                                        value={currentSetData.weight || ''}
                                        onChange={(e) => handleWeightChange(currentExercise.id, setKey, e.target.value)}
                                    />
                                    <span className="unit">kg</span>
                                </div>
                                <button
                                    className={`check-button ${isDone ? 'checked' : ''}`}
                                    onClick={() => handleSetToggle(currentExercise.id, setKey, !isDone)}
                                >
                                    {isDone ? 'Completado' : 'Check'}
                                </button>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* Footer Action */}
            <div className="player-footer">
                <button className="pill-button pill-button-primary full-width" onClick={handleNext}>
                    {isLastExercise ? 'Finalizar Entrenamiento' : 'Siguiente Ejercicio'}
                </button>
            </div>
        </div>
    );
}

export default RoutinePlayer;
